import { Data2 } from "../../../features/data-types"
import { useCustomerBuilders } from "../builders/customerBuilders"
import { useCustomerLoaders } from "../loaders/customerLoaders"
import { useCustomerActions } from "../actions/customerActions"
import { useAddressBuilders } from "../../../features/address"
import { Emitter, Event } from "../../../features/event-emitter"
import { useLocalStorageActions } from "../../../features/ui"
import { Customer } from "../state/types"

export const useCustomerService = () => {
    const { buildCustomer, buildCreditAccount } = useCustomerBuilders()
    const { buildAddress } = useAddressBuilders()

    const customerLoaders = useCustomerLoaders()

    const { saveCustomer } = useCustomerActions()
    const { getNewCustomerData, removeNewCustomerData } = useLocalStorageActions()

    const createCustomer = async (
        customerData: Data2,
        mainAddressData?: Data2,
        deliveryAddressesData?: Data2[],
        creditAccountData?: Data2
    ) => {
        const deliveryAddresses = (deliveryAddressesData || []).map(deliveryAddressData => buildAddress(deliveryAddressData))
        const newCustomerData: Data2 = {
            ...customerData,
            deliveryAddresses
        }
        if (mainAddressData) {
            const mainAddress = buildAddress(mainAddressData)
            newCustomerData.mainAddress = mainAddress
        }
        if (creditAccountData) {
            newCustomerData.creditAccount = buildCreditAccount(creditAccountData)
        }

        const customer = buildCustomer(newCustomerData)
        
        return saveCustomer(customer)
    }

    const updateCustomer = async (
        customerData: Data2,
        mainAddressData?: Data2,
        deliveryAddressesData?: Data2[],
        creditAccountData?: Data2
    ) => {
        const customerId = customerData.id! as string
        const deliveryAddresses = (deliveryAddressesData || []).map(deliveryAddressData => buildAddress(deliveryAddressData))
        let mainAddress = mainAddressData ? buildAddress(mainAddressData) : undefined
        let creditAccount = creditAccountData ? buildCreditAccount(creditAccountData) : undefined

        return updateCustomerProps(customerId, {
            ...customerData,
            deliveryAddresses,
            ...(mainAddress && { mainAddress }),
            ...(creditAccount && { creditAccount })
        })
    }

    const updateCustomerProps = async (
        customerId: string,
        props: Partial<Customer>,
    ) => {
        const { loadCustomer } = customerLoaders()
        const loadedCustomer = await loadCustomer(customerId, true)

        const customer = buildCustomer({
            ...loadedCustomer!,
            ...props
        })

        return saveCustomer(customer)
    }

    const quickCreateCustomer = async () => {
        const { customerData, creditAccountData } = getNewCustomerData()

        Emitter.emit(Event.DB_SUBMIT)
        const savedCustomer = await createCustomer(customerData, undefined, undefined, creditAccountData)
        Emitter.emit(Event.DB_SUBMIT_DONE, true, false)

        removeNewCustomerData()
        
        return savedCustomer
    }

    const archiveCustomer = async (customerId: string) => {
        const { loadCustomer } = customerLoaders()
        const customer = await loadCustomer(customerId, true)
        const archivedCustomer = buildCustomer({
            ...customer,
            archived: true
        })
        return saveCustomer(archivedCustomer)
    }

    return {
        createCustomer,
        updateCustomer,
        updateCustomerProps,
        quickCreateCustomer,
        archiveCustomer
    }
}
